<script setup lang="ts">
/**
 * Module: web/src/components/AppIcon.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.7.0
 * Date: 2026-07-11
 *
 * Minimal inline-SVG icon atom (stroke-based, currentColor). Self-contained --
 * no icon CDN / runtime fetch (unlike @iconify/vue), which keeps the deployed
 * SPA fully offline-capable behind the company proxy.
 * v1.1.0: + phone / pin / play / bolt / edit / search (MO entry wizard).
 * v1.2.0: + download (Manager Review audio download affordance).
 * v1.3.0: + help (circled ?, the HelpTip atom + /help nav) / book (help
 * section category affordance).
 * v1.4.0 (P10 Call History): + phone-clock (nav glyph -- handset + history
 * clock), call-in / call-out / call-internal (direction icons in the call
 * list + detail modal, contract call-history-api s6 "icon + label").
 * v1.5.0 (P8 polish): + star (Review Incentives nav glyph -- backlog #130;
 * also replaces the raw U+2605 chars in the Review Incentives headings).
 * v1.6.0 (P11 call attribution): + bell (journey "rang ext" rows) /
 * phone-off (journey hangup rows) -- the Call History journey timeline.
 * v1.7.0 (P12 System Config): + cog (nav glyph) / copy (redirect-URI copy
 * affordance in the GoTo wizard) / external (open-consent-in-new-tab hint).
 * v1.8.0 (Module Settings): + sliders (nav glyph -- three adjustment rails;
 * distinct from System Config's cog: knobs vs credentials).
 */
export type IconName =
  | 'menu' | 'close' | 'user' | 'users' | 'logout' | 'key' | 'shield'
  | 'chart' | 'plus' | 'list' | 'robot' | 'chevron-down' | 'eye' | 'eye-off'
  | 'sun' | 'moon' | 'lock' | 'back' | 'check' | 'alert'
  | 'phone' | 'pin' | 'play' | 'bolt' | 'edit' | 'search' | 'download'
  | 'help' | 'book'
  | 'phone-clock' | 'call-in' | 'call-out' | 'call-internal'
  | 'star' | 'bell' | 'phone-off'
  | 'cog' | 'copy' | 'external' | 'sliders';

defineProps<{
  name: IconName;
}>();
</script>

<template>
  <svg
    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
    stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
  >
    <template v-if="name === 'menu'">
      <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
    </template>
    <template v-else-if="name === 'close'">
      <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
    </template>
    <template v-else-if="name === 'user'">
      <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
    </template>
    <template v-else-if="name === 'users'">
      <circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c0-3.5 3-5 6.5-5s6.5 1.5 6.5 5" />
      <circle cx="17" cy="9" r="2.5" /><path d="M17.5 15.5c2.5.4 4 1.8 4 4.5" />
    </template>
    <template v-else-if="name === 'logout'">
      <path d="M14 4h-8v16h8" /><line x1="10" y1="12" x2="21" y2="12" /><polyline points="17,8 21,12 17,16" />
    </template>
    <template v-else-if="name === 'key'">
      <circle cx="8" cy="14" r="4" /><line x1="11" y1="11" x2="20" y2="2.5" transform="translate(0 2)" />
      <line x1="17" y1="6.5" x2="19.5" y2="9" />
    </template>
    <template v-else-if="name === 'shield'">
      <path d="M12 3l8 3v6c0 4.5-3.4 7.8-8 9-4.6-1.2-8-4.5-8-9V6z" />
    </template>
    <template v-else-if="name === 'chart'">
      <line x1="4" y1="20" x2="20" y2="20" /><line x1="7" y1="20" x2="7" y2="12" />
      <line x1="12" y1="20" x2="12" y2="6" /><line x1="17" y1="20" x2="17" y2="10" />
    </template>
    <template v-else-if="name === 'plus'">
      <rect x="4" y="4" width="16" height="16" rx="2" /><line x1="12" y1="8.5" x2="12" y2="15.5" /><line x1="8.5" y1="12" x2="15.5" y2="12" />
    </template>
    <template v-else-if="name === 'list'">
      <line x1="9" y1="6" x2="20" y2="6" /><line x1="9" y1="12" x2="20" y2="12" /><line x1="9" y1="18" x2="20" y2="18" />
      <circle cx="5" cy="6" r="1" /><circle cx="5" cy="12" r="1" /><circle cx="5" cy="18" r="1" />
    </template>
    <template v-else-if="name === 'robot'">
      <rect x="5" y="8" width="14" height="11" rx="2" /><line x1="12" y1="8" x2="12" y2="4" /><circle cx="12" cy="3.5" r="1" />
      <circle cx="9" cy="13" r="1" /><circle cx="15" cy="13" r="1" /><line x1="9.5" y1="16.5" x2="14.5" y2="16.5" />
    </template>
    <template v-else-if="name === 'chevron-down'">
      <polyline points="6,9 12,15 18,9" />
    </template>
    <template v-else-if="name === 'eye'">
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" /><circle cx="12" cy="12" r="2.5" />
    </template>
    <template v-else-if="name === 'eye-off'">
      <path d="M2 12s3.5-6 10-6c1.8 0 3.4.5 4.8 1.2M22 12s-3.5 6-10 6c-1.8 0-3.4-.5-4.8-1.2" />
      <line x1="4" y1="20" x2="20" y2="4" />
    </template>
    <template v-else-if="name === 'sun'">
      <circle cx="12" cy="12" r="4" /><line x1="12" y1="2.5" x2="12" y2="5" /><line x1="12" y1="19" x2="12" y2="21.5" />
      <line x1="2.5" y1="12" x2="5" y2="12" /><line x1="19" y1="12" x2="21.5" y2="12" />
      <line x1="5.3" y1="5.3" x2="7" y2="7" /><line x1="17" y1="17" x2="18.7" y2="18.7" />
      <line x1="5.3" y1="18.7" x2="7" y2="17" /><line x1="17" y1="7" x2="18.7" y2="5.3" />
    </template>
    <template v-else-if="name === 'moon'">
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4 8.5 8.5 0 1 0 20 14.5z" />
    </template>
    <template v-else-if="name === 'lock'">
      <rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </template>
    <template v-else-if="name === 'back'">
      <line x1="20" y1="12" x2="4" y2="12" /><polyline points="10,6 4,12 10,18" />
    </template>
    <template v-else-if="name === 'check'">
      <polyline points="4,13 9,18 20,6" />
    </template>
    <template v-else-if="name === 'alert'">
      <path d="M12 3L2.5 20h19z" /><line x1="12" y1="9" x2="12" y2="14" /><circle cx="12" cy="17" r="0.5" />
    </template>
    <template v-else-if="name === 'phone'">
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
    </template>
    <template v-else-if="name === 'pin'">
      <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" />
    </template>
    <template v-else-if="name === 'play'">
      <circle cx="12" cy="12" r="9" /><polygon points="10,8.5 16,12 10,15.5" />
    </template>
    <template v-else-if="name === 'bolt'">
      <polygon points="13,2.5 4.5,13.5 11,13.5 10,21.5 19.5,10.5 13,10.5" />
    </template>
    <template v-else-if="name === 'edit'">
      <path d="M4 20h4L20 8l-4-4L4 16z" /><line x1="13.5" y1="6.5" x2="17.5" y2="10.5" />
    </template>
    <template v-else-if="name === 'search'">
      <circle cx="10.5" cy="10.5" r="6.5" /><line x1="15.5" y1="15.5" x2="21" y2="21" />
    </template>
    <template v-else-if="name === 'download'">
      <line x1="12" y1="4" x2="12" y2="15" /><polyline points="7,10.5 12,15.5 17,10.5" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </template>
    <template v-else-if="name === 'help'">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.3a2.5 2.5 0 1 1 3.6 2.5c-.8.4-1.2 1-1.2 1.9v.3" />
      <circle cx="12" cy="16.8" r="0.5" />
    </template>
    <template v-else-if="name === 'book'">
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5z" />
      <path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H20" />
    </template>
    <template v-else-if="name === 'phone-clock'">
      <!-- handset lower-left + history clock upper-right -->
      <path d="M3.5 11.5h2.4l1.1 2.8-1.5 1a6.6 6.6 0 0 0 3.2 3.2l1-1.5 2.8 1.1v2.4a1.25 1.25 0 0 1-1.25 1.25A9.8 9.8 0 0 1 2.25 12.75 1.25 1.25 0 0 1 3.5 11.5z" />
      <circle cx="16" cy="7" r="5" />
      <polyline points="16,4.8 16,7 17.8,8.4" />
    </template>
    <template v-else-if="name === 'call-in'">
      <line x1="19" y1="5" x2="11" y2="13" /><polyline points="11,7.5 11,13 16.5,13" />
      <path d="M4 12.5h2.2l1 2.6-1.4.9a6.2 6.2 0 0 0 3 3l.9-1.4 2.6 1v2.2a1.2 1.2 0 0 1-1.2 1.2A9.3 9.3 0 0 1 2.8 13.7a1.2 1.2 0 0 1 1.2-1.2z" />
    </template>
    <template v-else-if="name === 'call-out'">
      <line x1="12" y1="12" x2="20" y2="4" /><polyline points="20,9.5 20,4 14.5,4" />
      <path d="M4 12.5h2.2l1 2.6-1.4.9a6.2 6.2 0 0 0 3 3l.9-1.4 2.6 1v2.2a1.2 1.2 0 0 1-1.2 1.2A9.3 9.3 0 0 1 2.8 13.7a1.2 1.2 0 0 1 1.2-1.2z" />
    </template>
    <template v-else-if="name === 'call-internal'">
      <polyline points="14,3.5 20,3.5 20,9.5" /><line x1="20" y1="3.5" x2="13" y2="10.5" />
      <polyline points="10,20.5 4,20.5 4,14.5" /><line x1="4" y1="20.5" x2="11" y2="13.5" />
    </template>
    <template v-else-if="name === 'star'">
      <polygon points="12,3.2 14.7,8.6 20.6,9.5 16.3,13.7 17.3,19.6 12,16.8 6.7,19.6 7.7,13.7 3.4,9.5 9.3,8.6" />
    </template>
    <template v-else-if="name === 'bell'">
      <path d="M12 3.5a5.5 5.5 0 0 0-5.5 5.5v4.2L4.8 16.5h14.4L17.5 13.2V9A5.5 5.5 0 0 0 12 3.5z" />
      <path d="M9.9 19.5a2.2 2.2 0 0 0 4.2 0" />
    </template>
    <template v-else-if="name === 'phone-off'">
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
      <line x1="5" y1="19" x2="19" y2="5" />
    </template>
    <template v-else-if="name === 'cog'">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M5.5 18.5l1.7-1.7M16.8 7.2l1.7-1.7" />
    </template>
    <template v-else-if="name === 'copy'">
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4.5A2.5 2.5 0 0 1 2 12.5v-8A2.5 2.5 0 0 1 4.5 2h8A2.5 2.5 0 0 1 15 4.5V5" />
    </template>
    <template v-else-if="name === 'external'">
      <path d="M13 5h6v6" /><line x1="19" y1="5" x2="10" y2="14" />
      <path d="M17 13v6H5V7h6" />
    </template>
    <template v-else-if="name === 'sliders'">
      <line x1="5" y1="4" x2="5" y2="20" /><circle cx="5" cy="9" r="2" />
      <line x1="12" y1="4" x2="12" y2="20" /><circle cx="12" cy="15" r="2" />
      <line x1="19" y1="4" x2="19" y2="20" /><circle cx="19" cy="7" r="2" />
    </template>
  </svg>
</template>
